import { canUserAccessContabilidad } from './contabilidadAccess';

const CONTABILIDAD_HOME = '/contabilidad/reportes/compras-diferenciadas';
const CONTABILIDAD_PATH = /^\/contabilidad(\/|$)/;
const PROFILE_PATH = /^\/profile(\/|$)/;
const DASHBOARD_PATH = /^\/dashboard(\/|$)/;

export function isAccountantUser(user) {
  return Boolean(user && user.role === 'contador');
}

export function isContabilidadFrontendPath(pathname) {
  return CONTABILIDAD_PATH.test(pathname || '');
}

export function getAccountantHomePath() {
  return CONTABILIDAD_HOME;
}

/**
 * Alcance de menú y rutas para usuario con rol limitado o contador.
 */
export function getLimitedUserScope(user) {
  if (isAccountantUser(user)) {
    return {
      isLimited: false,
      isAccountant: true,
      hasWarehouses: false,
      hasPointOfSales: false,
      hasAnyModule: false,
      showGestion: false,
      showAlmacenes: false,
      showCompras: false,
      showVentas: false,
      showDashboard: false,
      showAdministracion: false,
      showAdministracionCompleta: false,
      showRegistroActividad: false,
    };
  }

  if (!user || user.role !== 'limitado') {
    return {
      isLimited: false,
      isAccountant: false,
      hasWarehouses: true,
      hasPointOfSales: true,
      hasAnyModule: true,
      showGestion: true,
      showAlmacenes: true,
      showCompras: true,
      showVentas: true,
      showDashboard: true,
      showAdministracion: true,
      showAdministracionCompleta: true,
      showRegistroActividad: true,
    };
  }

  const warehouseIds = (Array.isArray(user.warehouse_ids) ? user.warehouse_ids : [])
    .filter((id) => id != null && id !== '');
  const pointOfSaleIds = (Array.isArray(user.point_of_sale_ids) ? user.point_of_sale_ids : [])
    .filter((id) => id != null && id !== '');
  const access = user.module_access || {};
  /** Al menos un almacén asignado → menú Almacenes. */
  const hasWarehouses = warehouseIds.length > 0;
  /** Al menos un POS asignado → menú Ventas. */
  const hasPointOfSales = pointOfSaleIds.length > 0;
  const hasAnyModule = Boolean(
    access.machinery || access.produccion_concesionaria || access.restaurantes
  );

  /** Sin almacenes ni módulos: solo Compras (y Ventas si tiene POS). */
  const comprasOnly = !hasWarehouses && !hasAnyModule;

  return {
    isLimited: true,
    isAccountant: false,
    hasWarehouses,
    hasPointOfSales,
    hasAnyModule,
    comprasOnly,
  /** Gestión consultable; Compras con permiso completo. */
    showGestion: hasWarehouses || hasAnyModule,
    showAlmacenes: hasWarehouses,
    showCompras: true,
    showVentas: hasPointOfSales,
    showDashboard: true,
    /** Menú Administración solo con Trazabilidad (consulta). */
    showAdministracion: true,
    showAdministracionCompleta: false,
    showRegistroActividad: true,
  };
}

/** Contador: solo Contabilidad, perfil y (si llega) dashboard. */
export function isPathAllowedForAccountant(pathname) {
  const path = pathname || '';
  return (
    isContabilidadFrontendPath(path) ||
    PROFILE_PATH.test(path) ||
    DASHBOARD_PATH.test(path)
  );
}

/** Rutas frontend del módulo Compras (permiso completo para rol limitado). */
const COMPRAS_PATH =
  /^\/(suppliers|requirements|purchase-orders|service-orders|continuous-services|documents|supply-continuous-documents|compras)(\/|$)/;

const VENTAS_PATH = /^\/ventas(\/|$)/;

const GESTION_PATH = /^\/(classification|entities|products|services)(\/|$)/;
const SUNAT_MAPPINGS_PATH = /^\/sunat-mappings(\/|$)/;
const ALMACEN_PATH = /^\/(warehouses|reports)(\/|$)/;
const AUDIT_PATH = /^\/audit(\/|$)/;
const ADMIN_PATH = /^\/administration(\/|$)/;
const TRACEABILITY_PATH =
  /^\/administration\/(document|purchase-order|service-order|entry-guide|requirement)-traceability(\/|$)/;
const ACTIVITY_LOGS_PATH = /^\/administration\/activity-logs(\/|$)/;

/** Guías y documentos del almacén (crear/editar en almacenes asignados). */
const WAREHOUSE_GUIDES_PATH =
  /^\/warehouses\/(\d+)\/(documents|entry-guides|initial-guides|exit-guides|transfer-guides|reception-guides|adjustment-guides)(\/|$)/;

export function isWarehouseGuidesFrontendPath(pathname) {
  return WAREHOUSE_GUIDES_PATH.test(pathname || '');
}

/**
 * Rol limitado con almacenes asignados: puede operar guías solo en esos almacenes.
 */
export function canLimitedUserMutateWarehouseGuides(pathname, user) {
  if (!user || user.role !== 'limitado') {
    return false;
  }
  const scope = getLimitedUserScope(user);
  if (!scope.showAlmacenes) {
    return false;
  }

  const match = (pathname || '').match(WAREHOUSE_GUIDES_PATH);
  if (!match) {
    return false;
  }

  const warehouseId = match[1];
  const allowed = new Set(
    (Array.isArray(user.warehouse_ids) ? user.warehouse_ids : [])
      .filter((id) => id != null && id !== '')
      .map((id) => String(id)),
  );

  return allowed.has(warehouseId);
}

const KARDEX_INCONSISTENCIES_PATH = /^\/reports\/kardex-inconsistencies(\/|$)/;
const KARDEX_RECALCULATE_PATH = /^\/reports\/kardex\/\d+\/(\d+)\/recalculate(\/|$)/;

/**
 * Rol limitado con almacenes: puede recalcular kardex en inconsistencias y en pares asignados.
 */
export function canLimitedUserMutateKardex(pathname, user) {
  if (!user || user.role !== 'limitado') {
    return false;
  }
  const scope = getLimitedUserScope(user);
  if (!scope.showAlmacenes) {
    return false;
  }

  const path = pathname || '';
  if (KARDEX_INCONSISTENCIES_PATH.test(path)) {
    return true;
  }

  const match = path.match(KARDEX_RECALCULATE_PATH);
  if (!match) {
    return false;
  }

  const warehouseId = match[1];
  const allowed = new Set(
    (Array.isArray(user.warehouse_ids) ? user.warehouse_ids : [])
      .filter((id) => id != null && id !== '')
      .map((id) => String(id)),
  );

  return allowed.has(warehouseId);
}

export function isSunatMappingsFrontendPath(pathname) {
  return SUNAT_MAPPINGS_PATH.test(pathname || '');
}

/**
 * Rol limitado: puede gestionar mapeos SUNAT (flujo de compras / documentos).
 */
export function canLimitedUserMutateSunatMappings(pathname, user) {
  if (!user || user.role !== 'limitado') {
    return false;
  }

  return isSunatMappingsFrontendPath(pathname);
}

/**
 * Rol limitado con POS asignados: puede operar el módulo Ventas.
 */
export function canLimitedUserMutateVentas(pathname, user) {
  if (!user || user.role !== 'limitado') {
    return false;
  }
  const scope = getLimitedUserScope(user);
  return scope.showVentas && isVentasFrontendPath(pathname);
}

export function isGestionFrontendPath(pathname) {
  return GESTION_PATH.test(pathname || '');
}

export function isTraceabilityPath(pathname) {
  return TRACEABILITY_PATH.test(pathname || '');
}

/** @deprecated Use isTraceabilityPath */
export function isDocumentTraceabilityPath(pathname) {
  return isTraceabilityPath(pathname);
}

export function isComprasFrontendPath(pathname) {
  return COMPRAS_PATH.test(pathname || '');
}

export function isVentasFrontendPath(pathname) {
  return VENTAS_PATH.test(pathname || '');
}

/**
 * Para rol limitado: solo almacenes en user.warehouse_ids.
 * Otros roles: lista completa sin cambios.
 */
export function filterWarehousesForUser(warehouses, user) {
  const list = Array.isArray(warehouses) ? warehouses : [];
  if (!user || user.role !== 'limitado') {
    return list;
  }

  const ids = (Array.isArray(user.warehouse_ids) ? user.warehouse_ids : [])
    .filter((id) => id != null && id !== '');
  if (ids.length === 0) {
    return [];
  }

  const allowed = new Set(ids.map((id) => String(id)));
  return list.filter((wh) => allowed.has(String(wh.id)));
}

/**
 * Para rol limitado: solo puntos de venta en user.point_of_sale_ids.
 * Otros roles: lista completa sin cambios.
 */
export function filterPointOfSalesForUser(pointOfSales, user) {
  const list = Array.isArray(pointOfSales) ? pointOfSales : [];
  if (!user || user.role !== 'limitado') {
    return list;
  }

  const ids = (Array.isArray(user.point_of_sale_ids) ? user.point_of_sale_ids : [])
    .filter((id) => id != null && id !== '');
  if (ids.length === 0) {
    return [];
  }

  const allowed = new Set(ids.map((id) => String(id)));
  return list.filter((pos) => allowed.has(String(pos.id)));
}

/**
 * Para rol limitado: solo sedes de compra en user.purchase_site_ids.
 * Otros roles: lista completa sin cambios.
 */
export function filterPurchaseSitesForUser(purchaseSites, user) {
  const list = Array.isArray(purchaseSites) ? purchaseSites : [];
  if (!user || user.role !== 'limitado') {
    return list;
  }

  const ids = (Array.isArray(user.purchase_site_ids) ? user.purchase_site_ids : [])
    .filter((id) => id != null && id !== '');
  if (ids.length === 0) {
    return [];
  }

  const allowed = new Set(ids.map((id) => String(id)));
  return list.filter((site) => allowed.has(String(site.id)));
}

export function isPathAllowedForLimitedUser(pathname, user) {
  const scope = getLimitedUserScope(user);
  if (!scope.isLimited) return true;

  if (isTraceabilityPath(pathname)) return true;
  if (ACTIVITY_LOGS_PATH.test(pathname)) return true;
  if (isContabilidadFrontendPath(pathname)) return canUserAccessContabilidad(user);
  if (isSunatMappingsFrontendPath(pathname)) return scope.showCompras;
  if (isVentasFrontendPath(pathname)) return scope.showVentas;
  if (ADMIN_PATH.test(pathname)) return false;
  if (!scope.showAlmacenes && ALMACEN_PATH.test(pathname)) return false;
  if (!scope.showAlmacenes && AUDIT_PATH.test(pathname)) return false;
  if (!scope.showGestion && GESTION_PATH.test(pathname)) return false;

  return true;
}
